import { useContext, useState } from "react"

import { 
  CustomSwitch, 
  DeleteConfirmationModal,
  Divider, 
  EventDateRangeBadge,
  OpenToParentsModal, 
  OpenToStaffModal,
  ReadOnlyBadge,
  ScheduleAction,
} from "@components"

import { eventAccessSchedules } from "@config"
import { NotificationContext } from "@context"

import { 
  DELETE_EVENT_SCHEDULED_ACTION_MUTATION,
  GET_STAFF_WITHOUT_SB_ACCOUNTS, 
  GET_STUDENTS_WITHOUT_GUARDIANS, 
  SAVE_EVENT_SCHEDULED_ACTION_MUTATION,
  SCHEDULED_ACTION_FRAGMENT,
} from "@graphql"

import { 
  getEventScheduleState, 
  hasExpired, 
  mySqlToLocal 
} from "@helpers"

import { useUpdateEvent } from "@hooks"
import { useLazyQuery, useMutation } from "@apollo/client"
import { Button } from "@heroui/react"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import ScheduledActionSuccessMessage from "../ScheduledActionSuccessMessage"
import PropTypes from "prop-types"
dayjs.extend(utc)

const EventAccess = ({ allowManualSbAccountMatching = false, backButton, event }) => 
{
  /**
   * Mutations
   */
  const [saveEventScheduledAction, { loading: savingSchedule }] = useMutation(SAVE_EVENT_SCHEDULED_ACTION_MUTATION, {
    update(cache, { data }) {
      if (!data?.saveEventScheduledAction) return

      cache.modify({
        id: cache.identify({ __typename: "Event", id: event.id }),

        fields: {
          scheduledActions(existingRefs = [], { readField }) {
            const newRef = cache.writeFragment({
              data: data.saveEventScheduledAction,
              fragment: SCHEDULED_ACTION_FRAGMENT,
            })

            const refs = existingRefs.filter(ref => String(readField("id", ref)) !== String(readField("id", newRef)))

            return [...refs, newRef]
          },
        },
      })
    },
  })

  // OnError handled by individual callers
  const [deleteEventScheduledAction, { loading: isDeletingSchedule }] = useMutation(DELETE_EVENT_SCHEDULED_ACTION_MUTATION, {
    update(cache, { data }, { variables }) {
      if (!data?.deleteEventScheduledAction) return

      cache.modify({
        id: cache.identify({
          __typename: "Event",
          id: event.id,
        }),

        fields: {
          scheduledActions(existingRefs = [], { readField }) {
            return existingRefs.filter(ref => String(readField("id", ref)) !== String(variables.input.id))
          },
        },
      })
    }
  })
    
  /**
   * State
   */
  const [currentScheduleType, setCurrentScheduleType] = useState(null)
  const [viewMode, setViewMode] = useState("DEFAULT") // DEFAULT | SCHEDULER | SCHEDULER_SUCCESS
  const [currentAction, setCurrentAction] = useState(null)
  
  const [scheduledActionSuccessMessage, setScheduledActionSuccessMessage] = useState({
    code: null,
    executeAt: null,
    advisories: [],
    advisoryLinkText: null,
    advisoryMessage: null,
    advisoryTitle: null,
    advisoryUrl: null,
    title: null,
  })

  const [scheduleMode, setScheduleMode] = useState("CREATE") // CREATE | EDIT
  const [showDeleteScheduleConfirmationModal, setShowDeleteScheduleConfirmationModal] = useState(false)
  const [showOpenToParentsModal, setShowOpenToParentsModal] = useState(false)
  const [showOpenToStaffModal, setShowOpenToStaffModal] = useState(false)

  /**
   * Context
   */
  const { notify, notifyError } = useContext(NotificationContext)

  /**
   * Hooks
   */
  const {updateEvent, loading: isUpdatingEvent} = useUpdateEvent()

  const {
    openStaffSchedule,
    openParentsSchedule,
    disabledParentBookingsSchedule,
  } = getEventScheduleState(event)

  const scheduledActionsByType = {
    staff: openStaffSchedule,
    parents: openParentsSchedule,
    disable_bookings: disabledParentBookingsSchedule,
  }

  /**
   * GraphQL Queries
   */
  const [getStaffWithoutSBAccounts, { loading: loadingStaffWithoutSBAccounts, data: staffWithoutSBAccounts }] = useLazyQuery(GET_STAFF_WITHOUT_SB_ACCOUNTS, {
    fetchPolicy: "no-cache",
  })

  const [getStudentsWithoutGuardians, { loading: loadingStudentsWithoutGuardians, data: orphanedStudents }] = useLazyQuery(GET_STUDENTS_WITHOUT_GUARDIANS, {
    fetchPolicy: "no-cache",
  })  
  

  /**
   * Functions
   */
  const canSchedule = (type) => dayjs().isBefore(getMaximumScheduleDate(type))
  
  const getMaximumScheduleDate = (type) => {
    if (type === "staff" || type === "parents") {
      return mySqlToLocal(event.start_date).subtract(1, "day")
    }

    return mySqlToLocal(event.start_date)
  }

  const getDescription = (type) => {
    const schedule = eventAccessSchedules[type]
    const currentScheduledAction = getScheduledAction(type)

    if (isReadOnlyMode) {
      return <p>{schedule.readOnlyText}</p>
    }

    if (isActionInProgress(schedule.field)) {
      return <p>{schedule.loadingText}</p>
    }

    if (currentScheduledAction && !event[schedule.field]) {
      return (
        <p>
          {schedule.scheduledText}

          <span className="font-semibold">
            {` ${mySqlToLocal(currentScheduledAction.execute_at).format("DD MMM YYYY [at] HH:mm")}. `}
          </span> 
          
          <Button 
            className="h-auto min-w-0 p-0 underline text-primary" 
            color="ghost"
            size="md"
            onPress={() => handleSetSchedule(type, "EDIT")}
          >
            Edit
          </Button>

          {" "}
          
          <Button 
            className="h-auto min-w-0 p-0 underline text-primary" 
            color="ghost"
            size="md"
            onPress={() => handleShowDeleteScheduleConfirmationModal(type)}
          >
            Delete
          </Button>
        </p>
      )
    }

    if (!canSchedule(type)) {
      return (
        <p>
          {schedule.inactiveText}
        </p>
      )
    }
      
    return event[schedule.field]
      ? <p>{schedule.activeText}</p>
      : <p>{schedule.inactiveText} or <Button className="h-auto min-w-0 px-0 underline text-primary" color="ghost" size="lg" onPress={() => handleSetSchedule(type, "CREATE")}>{schedule.scheduleLinkText}</Button></p>
  }
  
  const getScheduledAction = (type) => scheduledActionsByType[type] ?? null
   
  const hasScheduledAction = (type) => Boolean(scheduledActionsByType[type])

  // Function declaration used so it can be referenced by hooks declared above.  
  function handleCloseScheduler() {
    setViewMode("DEFAULT")
    setCurrentScheduleType(null)
    setScheduleMode("CREATE")
  }

  const handleDeleteEventSchedule = async (type) => {
    const currentScheduledAction = getScheduledAction(type)

    if (!currentScheduledAction) {
      throw new Error("No scheduled action found to delete.")
    }
    
    const response = await deleteEventScheduledAction({
      variables: {
        input: {
          id: currentScheduledAction.id
        }
      }
    })

    if (!response?.data?.deleteEventScheduledAction) {
      throw new Error("Failed to delete scheduled action.")
    }

    return response
  }

  const handleCloseDeleteScheduleConfirmationModal = () => {
    setCurrentScheduleType(null)
    setShowDeleteScheduleConfirmationModal(false)
  }

  const handleDeleteEventScheduleConfirmation = async () => {
    try {
      await handleDeleteEventSchedule(currentScheduleType)
      handleCloseDeleteScheduleConfirmationModal()
    } catch (error) {
      notifyError(error, "An error occurred while deleting the event schedule.")
    }
  }

  const handleToggleDisableParentBookings = async (isSelected) => {
    setCurrentAction("disable_parent_bookings")

    try {
      if (isSelected && hasScheduledAction("disable_bookings")) {
        await handleDeleteEventSchedule("disable_bookings")
      }
      
      await handleSave("disable_parent_bookings", isSelected)
    } catch (error) {
      notifyError(error, "An error occurred while updating parent booking availability.")
    } finally {
      setCurrentAction(null)
    }
  }

  const handleOpen = async (type, fieldName, onSuccess) => {
    try {
      if (hasScheduledAction(type)) {
        await handleDeleteEventSchedule(type)
      }
      
      await handleSave(fieldName, true)

      onSuccess()
    } catch (error) {
      notifyError(error, `An error occurred while opening the event to ${type}.`)
    } finally {
      setCurrentAction(null)
    }
  }

  const handleOpenToParents = () => handleOpen("parents", "open_parents", () => setShowOpenToParentsModal(false))
  const handleOpenToStaff = () => handleOpen("staff", "open_staff", () => setShowOpenToStaffModal(false))

  const handleCloseParentOrStaff = async (fieldName) => {
    try {
      await handleSave(fieldName, false)
    } catch (error) {
      notifyError(error, "An error occurred while closing the event access.")
    } finally {
      setCurrentAction(null)
    }
  }

  const handleOpenToParentsCheck = async (isSelected) => {
    setCurrentAction("open_parents")
    
    if (isSelected) {
      try {
        const response = await getStudentsWithoutGuardians({
          variables: {
            input: {
              event_id: event.id,
            }
          }
        })

        const studentsWithoutGuardians = response.data?.studentsWithoutGuardians ?? []

        if (studentsWithoutGuardians?.length > 0) {
          setShowOpenToParentsModal(true) 
        } else {
          handleOpenToParents()
        }
      } catch (error) {
        notifyError(error, "An error occurred while checking for students without guardians.")
        setCurrentAction(null)
      }
    } else {
      handleCloseParentOrStaff("open_parents")
    }
  }

  const handleOpenToStaffCheck = async (isSelected) => {
    setCurrentAction("open_staff")

    if (isSelected) {
      try {
        const response = await getStaffWithoutSBAccounts({
          variables: {
            input: {
              event_id: event.id,
            }
          }
        })

        const staffWithoutSBAccounts = response.data?.staffWithoutSBAccounts ?? []

        if (staffWithoutSBAccounts.length > 0 && allowManualSbAccountMatching) {
          setShowOpenToStaffModal(true) 
        } else {
          handleOpenToStaff()
        }
      } catch (error) {
        notifyError(error, "An error occurred while checking for staff without SchoolBooking accounts.")
        setCurrentAction(null)
      }
    } else {
      handleCloseParentOrStaff("open_staff")
    }
  }

  const handleSetSchedule = (type, schedulerMode) => {
    if (isReadOnlyMode) return

    setScheduleMode(schedulerMode)
    setViewMode("SCHEDULER")
    setCurrentScheduleType(type)
  }

  const handleShowDeleteScheduleConfirmationModal = (type) => {
    if (isReadOnlyMode) return
    setCurrentScheduleType(type)
    setShowDeleteScheduleConfirmationModal(true)
  }

  const handleUpdateEventSchedule = async (executeAt) => {
    const code = eventAccessSchedules[currentScheduleType].code
    const mySqlExecutedAt = dayjs(executeAt).format("YYYY-MM-DD HH:mm:00")
    const humanExecuteAt = dayjs(executeAt).format("DD MMMM YYYY [at] HH:mm")
    const titlesByCode = {
      OPEN_STAFF: "This event will open to staff on",
      OPEN_PARENTS: "This event will open to parents on",
      DISABLE_PARENT_BOOKINGS: "This event will close to parents on",
    }

    let results = {
      code: code,
      executeAt: humanExecuteAt,
      advisories: [],
      advisoryLinkText: null,
      advisoryMessage: null,
      advisoryTitle: null,
      advisoryUrl: null,
      title: titlesByCode[code] ?? null,
    }

    try {
      await saveEventScheduledAction({
        variables: {
          input: {
            event_id: event.id,
            code: code,
            execute_at: mySqlExecutedAt,
          }
        }
      })
    } catch (error) {
      notifyError(error, "An error occurred while updating the event schedule.")   
      return 
    }
  
    try {
      if (code === "OPEN_STAFF") {
        const { data } = await getStaffWithoutSBAccounts({ variables: {  input: { event_id: event.id }}})

        results = {
          ...results, 
          advisories: data.staffWithoutSBAccounts, 
          advisoryTitle: `${data.staffWithoutSBAccounts.length} staff member(s) do not have matching SchoolBooking accounts.`,
          advisoryMessage: `These staff members will not be able to access the event until they have been linked.`,
          advisoryUrl: "/data/staff",
          advisoryLinkText: "Review staff accounts"
        }
      }
  
      if (code === "OPEN_PARENTS") {
        const { data } = await getStudentsWithoutGuardians({ variables: {  input: { event_id: event.id }}})
        
        results = {
          ...results, 
          advisories: data.studentsWithoutGuardians, 
          advisoryTitle: `${data.studentsWithoutGuardians.length} students do not have linked guardians.`,
          advisoryMessage: `Assign guardians to these students before the event opens.`,
          advisoryUrl: `/event/${event.id}/invitations`,
          advisoryLinkText: "Review unmatched students"
        }
      }
    } catch (error) {
      // Ignore - schedule was created successfully and a daily email should be sent anyway
    }
  
    setViewMode("SCHEDULER_SUCCESS")
    setScheduledActionSuccessMessage(results)
  }

  const handleSave = (fieldName, value) => {
    return updateEvent({ 
      variables: {
        input: { id: event.id, [fieldName]: value }
      },
      
      onCompleted: () => notify({ title: "Success", type: "success", content: `Event updated successfully.`, open: true }),
      
      update(cache, { data }) {
        const updatedEvent = data?.updateEvent

        if (!updatedEvent) return

        cache.modify({
          id: cache.identify({ __typename: "Event", id: event.id }),

          fields: {
            [fieldName]() {
              return updatedEvent[fieldName]
            },
          },
        })
      }
    })
  }

  const isActionInProgress = (action) => currentAction === action && (isUpdatingEvent || isDeletingSchedule)
   
  const isReadOnlyMode = hasExpired(mySqlToLocal(event.end_date))
  const currentSchedule = currentScheduleType ? eventAccessSchedules[currentScheduleType] : null
  const minimumScheduleDate = new Date()
  const currentScheduledAction = getScheduledAction(currentScheduleType)
  const executeAt = currentSchedule && currentScheduledAction 
    ? mySqlToLocal(currentScheduledAction.execute_at).toDate() 
    : null
  
  const metaHeading = (
    <div className="flex flex-wrap justify-end gap-3 mb-6">
      {isReadOnlyMode && <ReadOnlyBadge isFullWidth size="lg" />}
      <EventDateRangeBadge size="lg" isFullWidth startDate={event?.start_date} endDate={event?.end_date} />
    </div>
  )

  return (
    <div>      
      {viewMode === "DEFAULT" && (
        <div>
          {metaHeading}

          <div className="flex items-center gap-3 mb-6">
            <InformationCircleIcon className="w-[4rem] h-auto" />
            
            <p className="text-sm text-foreground-400">
              <strong>Scheduling:</strong> Open access until 24 hours before event starts. Disable bookings until the event start.
            </p>
          </div>

          <CustomSwitch
            title="Open to staff"
            isSelected={event.open_staff}
            isLoading={loadingStaffWithoutSBAccounts || isActionInProgress("open_staff")}
            handleChange={handleOpenToStaffCheck}
            isDisabled={isReadOnlyMode}
          >
            {getDescription("staff")}
          </CustomSwitch>

          <Divider className="my-3" />

          <CustomSwitch
            title="Open to parents"
            isSelected={event.open_parents}
            handleChange={handleOpenToParentsCheck}
            isLoading={loadingStudentsWithoutGuardians || isActionInProgress("open_parents")}
            isDisabled={isReadOnlyMode}
          >
            {getDescription("parents")}
          </CustomSwitch>

          <Divider className="my-3" />
          
          <CustomSwitch
            title="Disable Parent Bookings"
            isSelected={event.disable_parent_bookings}
            handleChange={handleToggleDisableParentBookings}
            isDisabled={isReadOnlyMode}
            isLoading={isActionInProgress("disable_parent_bookings")}
          >
            {getDescription("disable_bookings")}
          </CustomSwitch>
          
          <div className="mt-6">            
            {backButton}
          </div>
        </div>
      )}

      {viewMode === "SCHEDULER" && (
        <div>
          {metaHeading}

          <ScheduleAction 
            title={currentSchedule?.title}
            description={currentSchedule?.description}
            handleSave={handleUpdateEventSchedule}
            handleCancel={handleCloseScheduler}
            minimumDateTime={minimumScheduleDate}
            maximumDateTime={getMaximumScheduleDate(currentScheduleType).toDate()}
            isReadOnly={isReadOnlyMode}
            isSubmitting={savingSchedule}
            mode={scheduleMode}
            executeAt={executeAt}
          />
        </div>
      )}

      {viewMode === "SCHEDULER_SUCCESS" && (
        <ScheduledActionSuccessMessage
          advisories={scheduledActionSuccessMessage.advisories}
          advisoryMessage={scheduledActionSuccessMessage.advisoryMessage}
          advisoryTitle={scheduledActionSuccessMessage.advisoryTitle}
          advisoryUrl={scheduledActionSuccessMessage.advisoryUrl}
          advisoryLinkText={scheduledActionSuccessMessage.advisoryLinkText}
          executeAt={scheduledActionSuccessMessage.executeAt}
          handleClose={() => setViewMode("DEFAULT")}
          title={scheduledActionSuccessMessage.title}
        />
      )}

      {showOpenToParentsModal && (
        <OpenToParentsModal 
          show 
          handleOpenToParents={handleOpenToParents}
          handleCancel={() => {
            setShowOpenToParentsModal(false)
            setCurrentAction(null)
          }}
          orphanedStudents={orphanedStudents?.studentsWithoutGuardians || []}
          isOpeningToParents={isActionInProgress("open_parents")}
        />
      )}

      {showOpenToStaffModal && (
        <OpenToStaffModal 
          show
          handleOpenToStaff={handleOpenToStaff}
          handleCancel={() => {
            setShowOpenToStaffModal(false)
            setCurrentAction(null)
          }}
          staffWithoutSBAccounts={staffWithoutSBAccounts?.staffWithoutSBAccounts || []} 
          isOpeningToStaff={isActionInProgress("open_staff")}
        />
      )}

      {showDeleteScheduleConfirmationModal && (
        <DeleteConfirmationModal
          title="Delete Schedule"
          subTitle={currentSchedule?.deleteSubTitle}
          handleClose={handleCloseDeleteScheduleConfirmationModal}
          handleDelete={handleDeleteEventScheduleConfirmation}
          isDeleting={isDeletingSchedule}
        >
          <div>
            <p className="mb-3 text-content2-foreground">
              Schedule is currently set to execute on <span className="font-semibold">{executeAt ? dayjs(executeAt).format("DD MMM YYYY [at] HH:mm") : "N/A"}</span>.
            </p>
            
            <p className="text-content2-foreground">{currentSchedule?.deleteDescription}</p>
          </div>
        </DeleteConfirmationModal>
      )}
    </div>
  )
}

EventAccess.propTypes = {
  allowManualSbAccountMatching: PropTypes.bool,
  backButton: PropTypes.element.isRequired,
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    open_staff: PropTypes.bool.isRequired,
    open_parents: PropTypes.bool.isRequired,
    disable_parent_bookings: PropTypes.bool.isRequired,
    start_date: PropTypes.string.isRequired,
    end_date: PropTypes.string.isRequired,
    scheduledActions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      execute_at: PropTypes.string.isRequired,
      
      status: PropTypes.shape({
        id: PropTypes.string.isRequired,
        code: PropTypes.string.isRequired,
      }).isRequired,

      type: PropTypes.shape({
        id: PropTypes.string.isRequired,
        code: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }).isRequired,
    })),
  }).isRequired,
}

export default EventAccess